import type { breakdownProcess } from "@/api/breakdownExchangeRateService";
import { TableCell, Checkbox } from "@mui/material";

interface BreakdownExchangeRateColumnProps {
  isAllSelected: boolean;
  handleSelectAll: (checked: boolean) => void;
  oemProcess: breakdownProcess[];
  spareProcess: breakdownProcess[];
  isOpenOEMOtherProcess: boolean;
  setIsOpenOEMOtherProcess: (open: boolean) => void;
  isOpenSpareOtherProcess: boolean;
  setIsOpenSpareOtherProcess: (open: boolean) => void;
}

const BreakdownExchangeRateColumn = ({
  isAllSelected,
  handleSelectAll,
  oemProcess,
  spareProcess,
  isOpenOEMOtherProcess,
  setIsOpenOEMOtherProcess,
  isOpenSpareOtherProcess,
  setIsOpenSpareOtherProcess,
}: BreakdownExchangeRateColumnProps) => {
  return (
    <>
      {/* Checkbox */}
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          top: 0,
          zIndex: 103,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <Checkbox
          checked={isAllSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          color="warning"
          sx={{
            backgroundColor: "white",
            borderRadius: "4px",
            "&:hover": { backgroundColor: "white" },
          }}
        />
      </TableCell>
      {/* Action */}
      <TableCell
        sx={{
          position: "sticky",
          left: 70,
          top: 0,
          zIndex: 103,
          width: 120,
          minWidth: 120,
          maxWidth: 120,
          textAlign: "center",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        Action
      </TableCell>
      {/* Part No. */}
      <TableCell
        sx={{
          position: "sticky",
          left: 190,
          top: 0,
          zIndex: 103,
          width: 200,
          minWidth: 200,
          maxWidth: 200,
          textAlign: "left",
          borderRight: "2px solid #e0e0e0",
        }}
      >
        Part No.
      </TableCell>
      {/* Part Name */}
      <TableCell
        sx={{
          position: "sticky",
          left: 390,
          top: 0,
          zIndex: 103,
          width: 200,
          minWidth: 200,
          maxWidth: 200,
          textAlign: "left",
          borderRight: "2px solid #e0e0e0",
        }}
      >
        Part Name
      </TableCell>
      {/* AC */}
      <TableCell
        sx={{
          position: "sticky",
          left: 590,
          top: 0,
          zIndex: 103,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          borderRight: "2px solid #e0e0e0",
        }}
      >
        AC
      </TableCell>
      {/* Routing */}
      <TableCell
        sx={{
          position: "sticky",
          left: 660,
          top: 0,
          zIndex: 103,
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          textAlign: "center",
          borderRight: "2px solid #e0e0e0",
        }}
      >
        Routing
      </TableCell>
      {/* OEM Price */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 150,
          textAlign: "center",
          borderRight: "2px solid #e0e0e0",
          fontWeight: 600,
        }}
      >
        OEM Price (THB)
      </TableCell>
      {/* Spare Price */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 150,
          textAlign: "center",
          borderRight: "2px solid #e0e0e0",
          fontWeight: 600,
        }}
      >
        Spare Price (THB)
      </TableCell>
      {/* Currency */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 300,
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
          fontWeight: 600,
        }}
      >
        Currency Code
      </TableCell>
      {/* Exchange Rate */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 120,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontWeight: 600,
        }}
      >
        Exchange Rate
      </TableCell>
      {/* Price */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontWeight: 600,
        }}
      >
        Price
      </TableCell>
      {/* Part Price */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontWeight: 600,
        }}
      >
        Part Price (THB)
      </TableCell>
      {/* OEM Other Process */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 200,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontWeight: 600,
          bgcolor: isOpenOEMOtherProcess ? "secondary.light" : "secondary.main",
          cursor: "pointer",
        }}
        onClick={() => {
          setIsOpenOEMOtherProcess(!isOpenOEMOtherProcess);
        }}
      >
        OEM Other Process (THB)
      </TableCell>
      {isOpenOEMOtherProcess &&
        oemProcess.map((process) => (
          <TableCell
            key={process.id}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 102,
              minWidth: 150,
              textAlign: "right",
              borderRight: "1px solid #e0e0e0",
              fontWeight: 600,
              bgcolor: "primary.light",
            }}
          >
            {process.name}
          </TableCell>
        ))}
      {/* Spare Other Process */}
      <TableCell
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 102,
          minWidth: 200,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontWeight: 600,
          bgcolor: isOpenSpareOtherProcess
            ? "secondary.light"
            : "secondary.main",
          cursor: "pointer",
        }}
        onClick={() => {
          setIsOpenSpareOtherProcess(!isOpenSpareOtherProcess);
        }}
      >
        Spare Other Process (THB)
      </TableCell>
      {isOpenSpareOtherProcess &&
        spareProcess.map((process) => (
          <TableCell
            key={process.id}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 102,
              minWidth: 150,
              textAlign: "right",
              borderRight: "1px solid #e0e0e0",
              fontWeight: 600,
              bgcolor: "primary.light",
            }}
          >
            {process.name}
          </TableCell>
        ))}
    </>
  );
};

export default BreakdownExchangeRateColumn;
